/** how a record READS on the console: its slot codes, its files, its colours — and a stock class as a record of its own */
import type { Classes, Drive, Entry, Kind, Rec } from './types'

/** the card's halftone ground (the film's orange · teal · green · purple), picked by the card's index */
export const GROUNDS = [
  ['#f2862e', '#8a2a5e'], ['#18b8a8', '#0c3b5e'], ['#a3d43a', '#1d5f3c'], ['#8457ff', '#1c206e'],
  ['#ef4f6b', '#4b1d6a'], ['#25c6e8', '#183f87'], ['#f7c843', '#a33a1d'], ['#4fd18b', '#123f55'],
]
export const ground = (i: number) => { const [a, b] = GROUNDS[((i % GROUNDS.length) + GROUNDS.length) % GROUNDS.length]; return `--g1:${a};--g2:${b}` }
export const portrait = (body: string) => `/portraits/${body}.png`
export const pad = (n: number, w = 2) => String(n).padStart(w, '0')
export const npcCode = (tenant: number) => `NPC_${pad(tenant, 4)}`

/** the left slots: its drives — the initial and where it starts (C80 = curiosity starts at 80), lit when it isn't the stock weight */
export function driveSlots(drives: Drive[]) {
  return drives.slice(0, 5).map((d) => ({ code: `${d.name[0].toUpperCase()}${Math.round(d.start)}`, label: d.name, sub: `×${d.weight}`, lit: d.weight !== 1 }))
}
/** the right slots: its layers, what it knows, its memories, its revision */
export function stateSlots(r: Pick<Rec, 'effective' | 'memories' | 'record'>) {
  const k = r.effective.knows
  return [
    { code: 'W1', label: 'world', sub: 'layer', lit: true },
    { code: r.effective.layers.includes('player') ? 'P1' : 'P0', label: 'player', sub: r.effective.layers.includes('player') ? 'glasses on' : 'layer', lit: r.effective.layers.includes('player') },
    { code: k === 'all' ? 'KA' : k === 'perceived' ? 'KP' : `K${k.length}`, label: 'knows', sub: Array.isArray(k) ? 'start list' : k, lit: Array.isArray(k) },
    { code: `M${Math.min(99, r.memories.count)}`, label: 'memory', sub: `${r.memories.count}`, lit: r.memories.count > 0 },
    { code: `R${pad(r.record.revision)}`, label: 'revision', sub: r.record.updatedAt ? new Date(r.record.updatedAt).toLocaleDateString() : 'stock', lit: r.record.revision > 0 },
  ]
}

/** the file an entry shows as in the record's grid (Keys's folders) */
export function fileOf(e: Entry): { name: string; ext: string } {
  const f = e.fn
  if (f === 'Blueprint.stamp') return { name: 'stamp', ext: 'logic' }
  if (f === 'NPC_Tree.class') return { name: 'class', ext: 'logic' }
  if (f.startsWith('Blueprint.')) return { name: f.slice(10), ext: 'obj' }
  if (f === 'Character_Fn.layers') return { name: 'layers', ext: 'tree' }
  if (f === 'Character_Fn.knows') return { name: 'knows', ext: 'dat' }
  if (f.startsWith('Character_Fn.card.')) return { name: f.slice(18), ext: 'txt' }
  if (f.startsWith('NPC_Tree.known.')) return { name: f.slice(15).toLowerCase().replace(/\s+/g, '_'), ext: 'node' }
  if (f.startsWith('Character_Fn.layers.')) return { name: `layer_${f.slice(20)}`, ext: 'tree' }
  if (f.startsWith('Mind.persona.')) return { name: f.slice(13), ext: 'mind' }
  if (f.startsWith('Mind.traits.')) return { name: f.slice(12), ext: 'trait' }
  const m = /^Character_Fn\.drives\.([a-z0-9_-]+)(?:\.(weight|start))?$/.exec(f)
  if (m) return { name: `${m[1]}${m[2] ? (m[2] === 'weight' ? '.wt' : '.st') : ''}`, ext: 'fn' }
  return { name: f.split('.').pop() ?? f, ext: 'dat' }
}
/** the folder (the tree on the right) each kind of change lands in */
export const FOLDER_OF: Record<Kind, string> = { logic: 'NPC_Tree', tree: 'Character_Fn', input: 'Character_Fn', entry: 'Additional_Entries', readout: 'Readout', mind: 'Mind' }
export const folderOfEntry = (e: Entry) => (e.fn.startsWith('Blueprint.') ? 'Blueprint' : FOLDER_OF[e.kind])

/** a value as a line of the change's diff */
export const show = (v: unknown) => (v === null || v === undefined ? '∅' : typeof v === 'string' ? JSON.stringify(v) : JSON.stringify(v))

/** a STOCK CLASS as a record (the Barista's one Logic file): what the class runs as, never saved */
export function stockRecord(cls: string, c: Classes): Rec {
  const def = c.classes[cls]
  const drives: Drive[] = c.baseDrives.map((d) => ({ ...d, weight: 1 }))
  for (const [n, d] of Object.entries(def.role?.drives ?? {})) {
    const i = drives.findIndex((x) => x.name === n)
    const base = i >= 0 ? drives[i] : { name: n, start: 0, rate: d.rate ?? 0, weight: 1 }
    const next = { ...base, ...(d.start != null ? { start: d.start } : {}), ...(d.rate != null ? { rate: d.rate } : {}), ...(d.weight != null ? { weight: d.weight } : {}) }
    if (i >= 0) drives[i] = next; else drives.push(next)
  }
  return {
    tenant: 0, relation: 'grant', editable: false, name: def.title, title: def.title, agentName: null, stock: true,
    record: { class: cls, template: { ...c.kit, role: def.role }, inputs: {}, revision: 0, createdBy: null, createdAt: null, updatedAt: null },
    effective: { body: def.body, home: null, tagline: def.tagline, greeting: def.greeting, drives, layers: def.role?.layers ?? ['world'], knows: def.role?.knows ?? 'perceived' },
    role: def.role ?? { format: 'simulacrum.role.v1', name: cls }, journal: [], folders: {}, memories: { count: 0, recent: null },
  }
}
